import { createContext, useState, useContext, useEffect, useRef, useCallback, useMemo } from 'react';
import { supabase } from '@/lib/supabaseClient';
import { useTutorial } from '@/lib/TutorialContext';
import { GUEST_PROFILE_KEY, PUBLIC_GUEST_MODE } from '@/lib/guestMode';

const AUTH_REQUEST_TIMEOUT_MS = 8000;
const PUBLIC_DEMO = PUBLIC_GUEST_MODE;

function readGuestProfile() {
  const defaultProfile = {
    display_name: 'Guest', map_theme: 'light', map_dark_mode: false,
    show_level: false, join_rankings: false,
  };
  try {
    return { ...defaultProfile, ...(JSON.parse(localStorage.getItem(GUEST_PROFILE_KEY)) || {}) };
  } catch {
    return defaultProfile;
  }
}

function withTimeout(promise, timeoutMs = AUTH_REQUEST_TIMEOUT_MS) {
  let timeoutId;
  const timeout = new Promise((_, reject) => {
    timeoutId = setTimeout(() => reject(new Error('Authentication request timed out')), timeoutMs);
  });
  return Promise.race([promise, timeout]).finally(() => clearTimeout(timeoutId));
}

const getSupabaseStorageKey = () => {
  try {
    for (let i = 0; i < localStorage.length; i++) {
      const key = localStorage.key(i);
      if (key && key.startsWith('sb-') && key.endsWith('-auth-token')) return key;
    }
  } catch {}
  return null;
};

const EMPTY_AUTH_CONTEXT = {
  user: null,
  profile: null,
  isAuthenticated: false,
  isLoadingAuth: false,
  authChecked: false,
  profileReady: false,
  isAdmin: false,
  isBanned: false,
  maintenanceMode: false,
  maintenanceMessage: '',
  logout: async () => {},
  navigateToLogin: () => {},
  refreshProfile: async () => {},
  syncProfile: () => {},
  refreshMaintenanceSettings: async () => {},
};

const AuthContext = createContext(EMPTY_AUTH_CONTEXT);

export const AuthProvider = ({ children }) => {
  const { active: tutorialActive } = useTutorial();
  // Optimistic auth: if Supabase has a persisted session in localStorage, the
  // user is returning. Render the authenticated app immediately (from cached
  // profile + cached entity data + IndexedDB GeoJSON) instead of staring at a
  // blank screen while getSession() does its network round-trip.
  const hasCachedSession = !!getSupabaseStorageKey();

  const [user, setUser] = useState(null);
  const [profile, setProfile] = useState(() => PUBLIC_DEMO ? readGuestProfile() : null);
  const [isAuthenticated, setIsAuthenticated] = useState(hasCachedSession || PUBLIC_DEMO);
  const [isLoadingAuth, setIsLoadingAuth] = useState(!hasCachedSession && !PUBLIC_DEMO);
  const [authChecked, setAuthChecked] = useState(hasCachedSession || PUBLIC_DEMO);
  const [profileReady, setProfileReady] = useState(PUBLIC_DEMO);
  const [maintenanceMode, setMaintenanceMode] = useState(false);
  const [maintenanceMessage, setMaintenanceMessage] = useState('');
  // Guards against the duplicate fetch that happens on load: getSession()
  // and onAuthStateChange(INITIAL_SESSION) both fire for the same user.
  const fetchingProfileRef = useRef(null);
  const profileRequestRef = useRef(0);
  // Tracks whether the user explicitly called logout() — if so, SIGNED_OUT
  // is intentional and we should sign out immediately without recovery.
  const intentionalLogoutRef = useRef(false);
  // Prevents infinite recovery loops: only one recovery attempt per spurious
  // SIGNED_OUT event.
  const recoverAttemptedRef = useRef(false);

  // --- Session backup / recovery ---
  // On refresh, if the access token is expired, Supabase tries to refresh it.
  // If the refresh fails (transient network error), Supabase fires SIGNED_OUT
  // and clears the session from localStorage — the user is logged out even
  // though the refresh token is still valid. To work around this, we
  // proactively save the session to a backup key on every auth state change.
  // On a spurious SIGNED_OUT, we restore the backup and retry getSession(),
  // giving the network a second chance to recover.
  const clearSessionBackup = useCallback(() => {
    try { localStorage.removeItem('sb-session-backup'); } catch {}
  }, []);

  const fetchProfile = useCallback(async (userId) => {
    if (fetchingProfileRef.current === userId) return;
    fetchingProfileRef.current = userId;
    const requestId = ++profileRequestRef.current;
    try {
      const { data, error } = await supabase.from('profiles').select('*').eq('id', userId).maybeSingle();
      if (error) {
        console.error('[auth] profile load failed:', error);
        return;
      }
      const { data: { session } } = await supabase.auth.getSession();
      const currentUser = session?.user;
      if (requestId !== profileRequestRef.current || currentUser?.id !== userId) return;
      setProfile(data || null);
      setProfileReady(true);
      if (!data) {
        // A valid auth session without a profile row means the account was
        // deleted or provisioned incompletely. Do not leave the user inside
        // a half-authenticated app that cannot load Settings or user data.
        intentionalLogoutRef.current = true;
        setUser(null);
        setIsAuthenticated(false);
        clearSessionBackup();
        await supabase.auth.signOut().catch(() => {});
      }
    } catch (err) {
      console.error('[auth] profile fetch crashed:', err);
    } finally {
      fetchingProfileRef.current = null;
      setProfileReady(true);
    }
  }, [clearSessionBackup]);

  const fetchMaintenanceSettings = useCallback(async () => {
    try {
      const { data } = await supabase.from('app_settings').select('*').eq('id', 1).maybeSingle();
      if (data) {
        setMaintenanceMode(!!data.maintenance_mode);
        setMaintenanceMessage(data.maintenance_message || '');
      }
    } catch {}
  }, []);

  const saveSessionBackup = useCallback(() => {
    try {
      const key = getSupabaseStorageKey();
      if (key) {
        const sessionStr = localStorage.getItem(key);
        if (sessionStr) localStorage.setItem('sb-session-backup', sessionStr);
      }
    } catch {}
  }, []);

  // Restore the session from backup and retry getSession(). Returns true if
  // the session was successfully recovered.
  const tryRecoverSession = useCallback(async () => {
    try {
      const backupStr = localStorage.getItem('sb-session-backup');
      if (!backupStr) return false;
      const key = getSupabaseStorageKey() || 'sb-yjzkknurgpwefmsjhpyx-auth-token';
      localStorage.setItem(key, backupStr);
      const { data: { session } } = await supabase.auth.getSession();
      if (session) {
        setUser(session.user);
        setIsAuthenticated(true);
        fetchProfile(session.user.id);
        saveSessionBackup();
        return true;
      }
    } catch {}
    return false;
  }, [fetchProfile, saveSessionBackup]);

  const signOutForReal = useCallback(() => {
    setUser(null);
    setProfile(null);
    setIsAuthenticated(false);
    clearSessionBackup();
  }, [clearSessionBackup]);

  useEffect(() => {
    if (PUBLIC_DEMO) return undefined;
    // Get initial session
    withTimeout(supabase.auth.getSession()).then(async ({ data: { session } }) => {
      try {
        if (session) {
          // A stale session with an expired access token leaves the user
          // "authenticated" but every mutation fails RLS — auth.uid() resolves
          // to null for an expired JWT, so the BEFORE-INSERT trigger writes a
          // null user_id and the WITH CHECK rejects the row. Refresh
          // proactively; if the refresh token is also invalid, clear the
          // session and send the user to login instead of leaving them stuck.
          const expiresAtMs = (session.expires_at || 0) * 1000;
          if (expiresAtMs && expiresAtMs < Date.now()) {
            // Token expired — try to refresh. If refresh fails (transient
            // network), keep the user signed in with the existing session;
            // autoRefresh and the mutation retry will recover when the network
            // returns. Never auto-log-out on a refresh failure.
            const { data: { session: refreshed } } = await withTimeout(supabase.auth.refreshSession());
            if (refreshed) session = refreshed;
          }
          setUser(session.user);
          setIsAuthenticated(true);
          setProfileReady(false);
          fetchProfile(session.user.id);
          saveSessionBackup();
        } else if (!recoverAttemptedRef.current) {
          // No session and not recovering — sign out. If recovery is in
          // progress (SIGNED_OUT already fired and triggered tryRecoverSession),
          // let the recovery handler decide.
          signOutForReal();
        }
        fetchMaintenanceSettings();
      } catch (err) {
        console.error('[auth] getSession error:', err);
      } finally {
        setIsLoadingAuth(false);
        setAuthChecked(true);
      }
    }).catch((err) => {
      console.error('[auth] getSession rejected:', err);
      setIsLoadingAuth(false);
      setAuthChecked(true);
    });

    // Listen for auth state changes. Only clear auth state on an explicit
    // SIGNED_OUT event — NOT on every null session. On spurious SIGNED_OUT
    // (e.g. transient token-refresh failure on page reload), attempt to
    // recover the session from backup before signing out.
    const { data: { subscription } } = supabase.auth.onAuthStateChange((event, session) => {
      try {
        if (session) {
          // TOKEN_REFRESHED fires every few minutes (autoRefreshToken). The user
          // identity hasn't changed — only the JWT did, which Supabase manages
          // internally. Skip setUser so we don't create a new object reference
          // that forces the whole app tree (including the map) to re-render,
          // which looks like a random reload.
          if (event !== 'TOKEN_REFRESHED') {
            setUser(session.user);
          }
          setIsAuthenticated(true);
          setProfileReady(false);
          saveSessionBackup();
          // Only re-fetch the profile on sign-in / initial load / user update —
          // not on every TOKEN_REFRESHED (fires every few min).
          if (event === 'SIGNED_IN' || event === 'INITIAL_SESSION' || event === 'USER_UPDATED') {
            fetchProfile(session.user.id);
          }
        } else if (event === 'SIGNED_OUT') {
          if (intentionalLogoutRef.current) {
            // Explicit logout() call — sign out for real.
            intentionalLogoutRef.current = false;
            signOutForReal();
          } else {
            // Spurious SIGNED_OUT (transient token-refresh failure). Try to
            // recover the session from backup. NEVER auto-log-out: if recovery
            // fails, keep the user signed in so they can retry — autoRefresh
            // will recover the token when the network returns. A second
            // SIGNED_OUT during recovery is ignored (guard below) so the
            // expired restored token can't cascade into a real sign-out that
            // would redirect to login (which looks like an app reload).
            if (!recoverAttemptedRef.current) {
              recoverAttemptedRef.current = true;
              tryRecoverSession().then(() => {
                recoverAttemptedRef.current = false;
              }).catch((err) => {
                recoverAttemptedRef.current = false;
                console.error('[auth] session recovery failed:', err);
              });
            }
          }
        }
        // Password recovery: clicking the reset link in the email establishes
        // a recovery session and fires this event. Supabase redirects to the
        // site root when /reset-password isn't in its allowlist, which would
        // land the user signed-in on the map. Catch it here and route them to
        // the reset page so they can actually set a new password.
        if (event === 'PASSWORD_RECOVERY' && window.location.pathname !== '/reset-password') {
          window.location.replace('/reset-password');
          return;
        }
        // INITIAL_SESSION with null: do nothing — SIGNED_OUT (which fires
        // before it) or getSession() handles the sign-out / recovery.
        setIsLoadingAuth(false);
        setAuthChecked(true);
      } catch (err) {
        console.error('[auth] onAuthStateChange error:', err);
        setIsLoadingAuth(false);
        setAuthChecked(true);
      }
    });

    return () => subscription.unsubscribe();
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // Realtime: keep the current user's profile in sync across devices/tabs.
  // When the profiles row changes (profile pic, display name, settings, etc.),
  // update the context immediately — no manual refreshProfile() needed.
  useEffect(() => {
    if (!user?.id) return;
    const channel = supabase.channel(`profile-sync-${user.id}`)
      .on('postgres_changes', {
        event: '*',
        schema: 'public',
        table: 'profiles',
        filter: `id=eq.${user.id}`,
      }, (payload) => {
        try {
          if (payload.eventType === 'DELETE') {
            setProfile(null);
          } else {
            const newProfile = payload.new;
            setProfile(newProfile);
          }
        } catch (err) { console.error('[realtime] profile-sync callback error:', err); }
      })
      .subscribe();
    return () => { supabase.removeChannel(channel); };
  }, [user?.id]);

  // Realtime: keep maintenance mode in sync — if an admin toggles it, all
  // users see the maintenance screen immediately without needing a refresh.
  useEffect(() => {
    if (PUBLIC_DEMO) return undefined;
    const channel = supabase.channel('app-settings-sync')
      .on('postgres_changes', {
        event: '*',
        schema: 'public',
        table: 'app_settings',
        filter: 'id=eq.1',
      }, (payload) => {
        try {
          if (payload.new) {
            setMaintenanceMode(!!payload.new.maintenance_mode);
            setMaintenanceMessage(payload.new.maintenance_message || '');
          }
        } catch (err) { console.error('[realtime] app-settings callback error:', err); }
      })
      .subscribe();
    return () => { supabase.removeChannel(channel); };
  }, []);

  const logout = useCallback(async (shouldRedirect = true) => {
    intentionalLogoutRef.current = true;
    await supabase.auth.signOut();
    signOutForReal();
    if (shouldRedirect) {
      window.location.href = '/login';
    }
  }, [signOutForReal]);

  const navigateToLogin = useCallback(() => {
    window.location.href = '/login';
  }, []);

  const refreshProfile = useCallback(async () => {
    const { data: { session } } = await supabase.auth.getSession();
    const u = session?.user;
    if (u) await fetchProfile(u.id);
  }, [fetchProfile]);

  const syncProfile = useCallback((nextProfile) => {
    if (!nextProfile) return;
    setProfile(nextProfile);
    if (PUBLIC_DEMO) {
      try { localStorage.setItem(GUEST_PROFILE_KEY, JSON.stringify(nextProfile)); } catch {}
    }
  }, []);

  const isAdmin = profile?.role === 'admin';
  const isBanned = profile?.is_banned === true;
  const tutorialProfile = useMemo(() => tutorialActive ? {
    ...(profile || {}),
    display_name: 'Alex Morgan',
    full_name: 'Alex Morgan',
    profile_picture: '/tutorial-profile.svg',
    map_theme: 'dark',
    map_dark_mode: true,
    show_level: true,
    join_rankings: true,
    tutorial_seen: true,
    tracking_token: 'demo-tracking-token',
  } : profile, [profile, tutorialActive]);

  const value = useMemo(() => ({
    user,
    profile: tutorialProfile,
    isAuthenticated,
    isLoadingAuth,
    authChecked,
    profileReady,
    isAdmin,
    isBanned,
    maintenanceMode,
    maintenanceMessage,
    logout,
    navigateToLogin,
    refreshProfile,
    syncProfile,
    refreshMaintenanceSettings: fetchMaintenanceSettings,
  }), [user, tutorialProfile, isAuthenticated, isLoadingAuth, authChecked, profileReady, isAdmin, isBanned, maintenanceMode, maintenanceMessage, logout, navigateToLogin, refreshProfile, syncProfile, fetchMaintenanceSettings]);

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => {
  return useContext(AuthContext) || EMPTY_AUTH_CONTEXT;
};